import ReportIssueBar from '@/components/ReportIssueBar';
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileText, Download, ArrowLeft, Menu, Settings, Upload, X } from "lucide-react";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import axios from "axios";
import toast from 'react-hot-toast';
import MatrixBackground from "@/MatrixBackground";
import AnimatedToolTitle from "@/components/AnimatedToolTitle";
import AnimatedActionButton from "@/components/AnimatedActionButton";
import LedProgressLine from "@/components/LedProgressLine";
import AccountSettings from "@/components/AccountSettings";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Remove old token function - now using global Firebase auth via axios interceptor

// Helper function to get user from localStorage
const getUser = () => {
  const user = localStorage.getItem('user');
  return user ? JSON.parse(user) : null;
};

const DocumentGeneration = ({ onBack }) => {
    // Set page title
  useEffect(() => {
    document.title = "Document Generation - Lawra AI";
    return () => {
      document.title = "Lawra AI - AI Legal Assistant for Lawyers & Law Firms | Save 70-80% Time";
    };
  }, []);

  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    documentType: '',
    sector: '',
    jurisdiction: '',
    additionalDetails: ''
  });
  const [generating, setGenerating] = useState(false);
  const [generatedDocument, setGeneratedDocument] = useState(null);
  const [templateFile, setTemplateFile] = useState(null);
  const [templateUploaded, setTemplateUploaded] = useState(false);
  const [showTemplateStep, setShowTemplateStep] = useState(true);
  const [user, setUser] = useState(getUser());
  const [trialUser, setTrialUser] = useState(null);
  const [accountSettingsOpen, setAccountSettingsOpen] = useState(false);
  const [accountSettingsTab, setAccountSettingsTab] = useState('account');
  
  useEffect(() => {
    const trialData = localStorage.getItem('trial_user');
    if (trialData) {
      setTrialUser(JSON.parse(trialData));
    }
  }, []);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleTemplateUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file type
      const allowedTypes = ['application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'application/msword'];
      if (!allowedTypes.includes(file.type)) {
        toast.error('Please upload a PDF or Word document');
        return;
      }
      
      // Check file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        toast.error('File size must be less than 10MB');
        return;
      }
      
      setTemplateFile(file);
      toast.success('Template uploaded successfully!');
    }
  };

  const handleSkipTemplate = () => {
    setShowTemplateStep(false);
  };

  const handleContinueWithTemplate = () => {
    if (templateFile) {
      setTemplateUploaded(true);
      setShowTemplateStep(false);
    } else {
      toast.error('Please upload a template file first');
    }
  };

  const handleGenerate = async () => {
    if (!formData.documentType || !formData.sector || !formData.jurisdiction) {
      toast.error('Please fill in all required fields');
      return;
    }

    setGenerating(true);

    try {
      // Prepare form data for file upload
      const requestData = new FormData();
      requestData.append('documentType', formData.documentType);
      requestData.append('sector', formData.sector);
      requestData.append('jurisdiction', formData.jurisdiction);
      requestData.append('additionalDetails', formData.additionalDetails);
      
      // Add template file if uploaded
      if (templateFile) {
        requestData.append('template', templateFile);
        requestData.append('useTemplate', 'true');
      }

      const response = await axios.post(
        `${API}/documents/generate`,
        requestData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      setGeneratedDocument(response.data);
      toast.success('Document generated successfully!');
    } catch (error) {
      console.error('Error generating document:', error);
      const errorMessage = error.response?.data?.detail || 'Error generating document';
      
      // Check if it's a trial limit error
      if (error.response?.status === 403 && errorMessage.includes('Trial limit')) {
        toast.error(errorMessage);
        setTimeout(() => {
          navigate('/dashboard?open=account-settings&tab=billing');
        }, 500);
      } else {
        toast.error(errorMessage);
      }
    } finally {
      setGenerating(false);
    }
  };

  const handleDownload = () => {
    if (!generatedDocument || !generatedDocument.content) {
      toast.error('No document to download');
      return;
    }
    
    const blob = new Blob([generatedDocument.content], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${formData.documentType.replace(/\s+/g, '_')}_${Date.now()}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('Document downloaded!');
  };

  const handleReset = () => {
    setFormData({
      documentType: '',
      sector: '',
      jurisdiction: '',
      additionalDetails: ''
    });
    setGeneratedDocument(null);
    setTemplateFile(null);
    setTemplateUploaded(false);
    setShowTemplateStep(true);
  };

  const handleLogout = () => {
    if (trialUser) {
      navigate('/auth', { state: { showRegister: true, trialUserData: trialUser } });
    } else {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      navigate('/');
    }
  };

  return (
    <div className="tool-page">
      <MatrixBackground />
      
      {/* Dashboard Header */}
      <div className="dashboard-header">
        <div className="header-content">
          <div className="header-left">
            <img src="/lawra-header-logo.png" alt="Lawra" className="header-logo-image" onClick={() => navigate('/dashboard')} style={{ cursor: 'pointer' }} />
          </div>
          
          <div className="header-right">
            {/* Mobile-only logo */}
            <img src="/lawra-header-logo.png" alt="Lawra" className="header-logo-image mobile-only" />
            
            <span className="user-name">
              Hello, <span className="gold-text">{trialUser?.name || user?.full_name}</span>
            </span>
            {trialUser && (
              <span className="trial-badge">Trial Mode</span>
            )}
            
            <Button 
              onClick={handleLogout}
              style={{
                background: 'linear-gradient(135deg, #D4AF37 0%, #E5B200 100%)',
                color: '#1a1a1a',
                border: 'none',
                fontWeight: '700',
                padding: '0.75rem 2rem',
                borderRadius: '50px',
                boxShadow: '0 4px 15px rgba(212, 175, 55, 0.4)',
                animation: 'signupPulse 2s ease-in-out infinite',
                whiteSpace: 'nowrap',
                minWidth: '100px'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'linear-gradient(135deg, #FFCE45 0%, #E5B200 100%)';
                e.currentTarget.style.boxShadow = '0 8px 30px rgba(212, 175, 55, 0.8)';
                e.currentTarget.style.transform = 'translateY(-2px) scale(1.05)';
                e.currentTarget.style.animation = 'none';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'linear-gradient(135deg, #D4AF37 0%, #E5B200 100%)';
                e.currentTarget.style.boxShadow = '0 4px 15px rgba(212, 175, 55, 0.4)';
                e.currentTarget.style.transform = 'scale(1)';
                e.currentTarget.style.animation = 'signupPulse 2s ease-in-out infinite';
              }}
            >
              {trialUser ? 'Sign Up' : 'Logout'}
            </Button>

            {/* Menu Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="menu-button">
                  <Menu size={20} />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="menu-dropdown">
                <DropdownMenuItem onClick={() => setAccountSettingsOpen(true)}>
                  <Settings size={16} className="mr-2" />
                  Account Settings
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>
      
      <div className="tool-header">
        <Button
          variant="ghost"
          onClick={() => onBack ? onBack() : navigate('/dashboard')}
          className="back-button"
        >
          <ArrowLeft size={20} />
          Back to Dashboard
        </Button>
        <div className="tool-title-section">
          <FileText size={40} className="tool-icon gold-icon" />
          <div>
            <AnimatedToolTitle title="Document Generation" />
            <p className="tool-subtitle">Generate legal documents in your branding with Lawra's AI assistance</p>
          </div>
        </div>
      </div>

      <div className="tool-content">
        {showTemplateStep ? (
          /* Template Upload Step */
          <Card className="template-upload-card">
            <CardHeader>
              <CardTitle style={{ color: '#1a1a1a' }}>Template Upload (Optional)</CardTitle>
              <CardDescription>
                Upload your law firm's branded template to maintain your company identity, or skip to generate a standard document
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="template-upload-section">
                <div className="file-upload-area">
                  <input
                    type="file"
                    id="template-upload"
                    accept=".pdf,.docx,.doc"
                    onChange={handleTemplateUpload}
                    style={{ display: 'none' }}
                  />
                  <label htmlFor="template-upload" className="file-upload-label">
                    <div className="upload-icon-tool">
                      {templateFile ? <FileText size={48} /> : <Upload size={48} />}
                    </div>
                    <p className="upload-text-tool">
                      {templateFile ? templateFile.name : 'Click to upload any branded document (PDF or DOCX) for Lawra to use'}
                    </p>
                    <p className="upload-subtext-tool">Maximum file size: 10MB</p>
                  </label>
                </div>
                
                {templateFile && (
                  <div className="template-file-preview" style={{ marginTop: '1rem', padding: '1rem', backgroundColor: 'rgba(212, 175, 55, 0.1)', borderRadius: '8px', border: '1px solid rgba(212, 175, 55, 0.3)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <FileText size={16} style={{ color: '#D4AF37' }} />
                        <span style={{ color: '#1a1a1a', fontSize: '0.9rem' }}>{templateFile.name}</span>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setTemplateFile(null);
                          document.getElementById('template-upload').value = '';
                        }}
                        style={{ color: '#D4AF37' }}
                      >
                        <X size={16} />
                      </Button>
                    </div>
                    <p style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.8rem', marginTop: '0.5rem', marginBottom: '0' }}>
                      Your branding and formatting will be preserved in the generated document
                    </p>
                  </div>
                )}

                {!templateFile && (
                  <div style={{ textAlign: 'center', margin: '1.5rem 0', color: '#1a1a1a', fontSize: '1rem', fontWeight: '500' }}>
                    Or
                  </div>
                )}

                <div className="template-actions" style={{ display: 'flex', gap: '1rem', justifyContent: 'center', marginTop: templateFile ? '2rem' : '0' }}>
                  {templateFile && (
                    <AnimatedActionButton
                      onClick={handleContinueWithTemplate}
                      icon={<FileText size={20} />}
                    >
                      Continue with Template
                    </AnimatedActionButton>
                  )}
                  {!templateFile && (
                    <AnimatedActionButton
                      onClick={handleSkipTemplate}
                      icon={<FileText size={20} />}
                    >
                      Generate Standard Document
                    </AnimatedActionButton>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ) : (
          /* Main Document Generation Form */
          <Card className="generation-form-card">
          <CardHeader>
            <CardTitle>Document Details</CardTitle>
            <CardDescription>
              Provide details about the document you need to generate
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="form-grid">
              <div className="form-group">
                <Label htmlFor="documentType">Document Type *</Label>
                <Input
                  id="documentType"
                  placeholder="e.g., Debt Recovery Letter, NDA, Service Agreement"
                  value={formData.documentType}
                  onChange={(e) => handleInputChange('documentType', e.target.value)}
                />
              </div>

              <div className="form-group">
                <Label htmlFor="sector">Sector / Service Area *</Label>
                <Input
                  id="sector"
                  placeholder="e.g., Employment Law, Commercial Contracts, Property Law"
                  value={formData.sector}
                  onChange={(e) => handleInputChange('sector', e.target.value)}
                />
              </div>

              <div className="form-group">
                <Label htmlFor="jurisdiction">Jurisdiction *</Label>
                <Input
                  id="jurisdiction"
                  placeholder="e.g., England & Wales, New York, EU"
                  value={formData.jurisdiction}
                  onChange={(e) => handleInputChange('jurisdiction', e.target.value)}
                />
              </div>

              <div className="form-group full-width">
                <Label htmlFor="additionalDetails">Additional Details</Label>
                <Textarea
                  id="additionalDetails"
                  placeholder="Provide any specific details, clauses, or requirements for the document..."
                  rows={6}
                  value={formData.additionalDetails}
                  onChange={(e) => handleInputChange('additionalDetails', e.target.value)}
                />
              </div>
            </div>

            <div className="form-actions">
              <AnimatedActionButton
                onClick={handleGenerate}
                disabled={generating}
                loading={generating}
                size="lg"
              >
                Generate Document
              </AnimatedActionButton>
              <LedProgressLine isActive={generating} />
            </div>
          </CardContent>
        </Card>
        )}

        {generatedDocument && (
          <Card className="result-card">
            <CardHeader>
              <div className="result-header">
                <CardTitle>Generated Document</CardTitle>
                <Button
                  onClick={handleDownload}
                  variant="outline"
                  size="sm"
                >
                  <Download size={16} className="mr-2" />
                  Download
                </Button>
              </div>
              <CardDescription>
                ✨ Generated by Lawra
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div 
                className="document-content" 
                dangerouslySetInnerHTML={{ __html: generatedDocument.content || '' }}
              />
              <div className="mt-6">
                <Button onClick={handleReset} variant="outline">
                  Generate New Document
                </Button>
        <ReportIssueBar toolName="Document Generation" />
              </div>
            </CardContent>
          </Card>
        )}
      </div>
      
      {/* Account Settings Modal */}
      <AccountSettings
        open={accountSettingsOpen}
        onOpenChange={setAccountSettingsOpen}
        user={user}
        trialUser={trialUser}
        defaultTab={accountSettingsTab}
      />
    </div>
  );
};

export default DocumentGeneration;
